/**
 * Low Stock Indicator
 *
 * Dynamically updates low stock messages when customers select different variants.
 * Works with Shopify's native variant selectors and custom implementations.
 *
 * @version 1.0.0
 */

(function() {
  'use strict';

  const LowStockIndicator = {
    /**
     * Initialize the low stock indicator system
     */
    init: function() {
      this.indicators = document.querySelectorAll('.low-stock-indicator[data-product-id]');

      if (this.indicators.length === 0) return;

      this.indicators.forEach(indicator => {
        this.setupIndicator(indicator);
      });

      // Listen for Shopify's native variant change event
      document.addEventListener('variant:changed', this.handleVariantChange.bind(this));

      // Listen for custom variant change events (common in many themes)
      document.addEventListener('variantChange', this.handleVariantChange.bind(this));

      // Observe URL changes for variant updates via URL parameter
      this.observeURLChanges();

      // Initialize with current variant if present in URL
      this.initFromURL();
    },

    /**
     * Setup individual indicator with its variant data
     * @param {HTMLElement} indicator - The indicator element
     */
    setupIndicator: function(indicator) {
      const productId = indicator.dataset.productId;
      const inventoryDataEl = document.querySelector(`[data-variant-inventory-${productId}]`);

      if (!inventoryDataEl) {
        console.warn(`Low Stock Indicator: No inventory data found for product ${productId}`);
        return;
      }

      try {
        indicator.variantInventory = JSON.parse(inventoryDataEl.textContent);
      } catch (e) {
        console.error('Low Stock Indicator: Failed to parse inventory data', e);
        return;
      }

      // Find associated variant selectors
      this.bindVariantSelectors(indicator, productId);

      // Initialize with the currently selected variant on page load
      this.initFromCurrentSelection(indicator, productId);
    },

    /**
     * Initialize indicator from currently selected variant in the form
     * @param {HTMLElement} indicator - The indicator element
     * @param {string} productId - The product ID
     */
    initFromCurrentSelection: function(indicator, productId) {
      // First check URL for variant parameter
      const urlParams = new URLSearchParams(window.location.search);
      const urlVariantId = urlParams.get('variant');

      if (urlVariantId && indicator.variantInventory[urlVariantId]) {
        this.updateIndicator(indicator, urlVariantId);
        return;
      }

      // Find the form and get the current variant ID from hidden input
      const form = document.querySelector(`form#product_form_${productId}`) ||
                   document.querySelector(`form[action*="/cart/add"] input[name="id"]`)?.closest('form') ||
                   indicator.closest('form[action*="/cart/add"]') ||
                   document.querySelector('form[action*="/cart/add"]');

      if (form) {
        const variantInput = form.querySelector('input[name="id"], select[name="id"]');
        if (variantInput && variantInput.value) {
          this.updateIndicator(indicator, variantInput.value);
          return;
        }
      }

      // Fallback: check for any hidden input with variant ID near the indicator
      const nearbyInput = document.querySelector(`input.input_variant[name="id"]`);
      if (nearbyInput && nearbyInput.value && indicator.variantInventory[nearbyInput.value]) {
        this.updateIndicator(indicator, nearbyInput.value);
      }
    },

    /**
     * Bind to variant selector elements
     * @param {HTMLElement} indicator - The indicator element
     * @param {string} productId - The product ID
     */
    bindVariantSelectors: function(indicator, productId) {
      // Common variant selector patterns used in Shopify themes
      const selectors = [
        // Standard Shopify selectors
        `[name="id"][form*="${productId}"]`,
        `select[name="id"]`,
        `input[name="id"]`,
        // Radio button variant selectors
        `input[type="radio"][name*="option"]`,
        // Common custom selectors
        `.variant-selector[data-product-id="${productId}"]`,
        `.product-form__input[data-product-id="${productId}"]`,
        // Size swatches (common pattern)
        `.size-swatch`,
        `.variant-swatch`,
        `.option-swatch`,
        // Dawn theme patterns
        `variant-selects[data-product-id="${productId}"]`,
        `variant-radios[data-product-id="${productId}"]`
      ];

      const form = document.querySelector(`form[action*="/cart/add"][data-product-id="${productId}"]`) ||
                   indicator.closest('form[action*="/cart/add"]') ||
                   document.querySelector('form[action*="/cart/add"]');

      if (form) {
        // Listen for changes on the form
        form.addEventListener('change', (e) => {
          this.handleFormChange(e, indicator, productId);
        });

        // Also listen for custom variant events on the form
        form.addEventListener('variant:change', (e) => {
          if (e.detail && e.detail.variant) {
            this.updateIndicator(indicator, e.detail.variant.id);
          }
        });
      }

      // Bind to individual selectors
      selectors.forEach(selector => {
        const elements = document.querySelectorAll(selector);
        elements.forEach(el => {
          el.addEventListener('change', (e) => {
            this.handleSelectorChange(e, indicator, productId);
          });
          el.addEventListener('click', (e) => {
            // Handle swatch clicks
            if (e.target.dataset.variantId) {
              this.updateIndicator(indicator, e.target.dataset.variantId);
            }
          });
        });
      });

      // Bind to Wokiee theme's swatch system (.tt-options-swatch)
      const swatchLists = document.querySelectorAll('.tt-options-swatch');
      swatchLists.forEach(list => {
        list.addEventListener('click', (e) => {
          const link = e.target.closest('a[data-value]');
          if (link) {
            // Delay slightly to let the theme update the hidden input
            setTimeout(() => {
              const variantInput = document.querySelector(`input.input_variant[name="id"]`);
              if (variantInput && variantInput.value) {
                this.updateIndicator(indicator, variantInput.value);
              }
            }, 50);
          }
        });
      });

      // Also observe changes to the hidden variant input directly
      const variantInput = document.querySelector(`input.input_variant[name="id"]`);
      if (variantInput) {
        // Use MutationObserver to detect value changes
        const observer = new MutationObserver(() => {
          if (variantInput.value) {
            this.updateIndicator(indicator, variantInput.value);
          }
        });
        observer.observe(variantInput, { attributes: true, attributeFilter: ['value'] });

        // Also listen for manual value changes via JavaScript
        const originalDescriptor = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value');
        const self = this;
        Object.defineProperty(variantInput, 'value', {
          get: function() {
            return originalDescriptor.get.call(this);
          },
          set: function(val) {
            originalDescriptor.set.call(this, val);
            self.updateIndicator(indicator, val);
          }
        });
      }
    },

    /**
     * Handle form change events
     * @param {Event} e - The change event
     * @param {HTMLElement} indicator - The indicator element
     * @param {string} productId - The product ID
     */
    handleFormChange: function(e, indicator, productId) {
      // Look for variant ID in the form
      const form = e.currentTarget;
      const variantInput = form.querySelector('input[name="id"], select[name="id"]');

      if (variantInput && variantInput.value) {
        this.updateIndicator(indicator, variantInput.value);
      }
    },

    /**
     * Handle individual selector changes
     * @param {Event} e - The change event
     * @param {HTMLElement} indicator - The indicator element
     * @param {string} productId - The product ID
     */
    handleSelectorChange: function(e, indicator, productId) {
      const target = e.target;

      // Direct variant ID
      if (target.name === 'id' || target.dataset.variantId) {
        const variantId = target.value || target.dataset.variantId;
        this.updateIndicator(indicator, variantId);
        return;
      }

      // For option-based selectors, we need to find the matching variant
      // This is handled by the form change event or URL observation
    },

    /**
     * Handle global variant change events
     * @param {CustomEvent} e - The variant change event
     */
    handleVariantChange: function(e) {
      if (!e.detail) return;

      const variant = e.detail.variant || e.detail;
      const productId = e.detail.productId || variant.product_id;

      if (variant && variant.id) {
        this.indicators.forEach(indicator => {
          if (!productId || indicator.dataset.productId === String(productId)) {
            this.updateIndicator(indicator, variant.id);
          }
        });
      }
    },

    /**
     * Observe URL changes for variant parameter updates
     */
    observeURLChanges: function() {
      // Use MutationObserver to detect URL changes in SPAs
      let lastURL = window.location.href;

      const checkURL = () => {
        if (window.location.href !== lastURL) {
          lastURL = window.location.href;
          this.initFromURL();
        }
      };

      // Check on popstate (back/forward navigation)
      window.addEventListener('popstate', checkURL);

      // Periodically check for URL changes (for pushState/replaceState)
      setInterval(checkURL, 250);
    },

    /**
     * Initialize indicator from URL variant parameter
     */
    initFromURL: function() {
      const urlParams = new URLSearchParams(window.location.search);
      const variantId = urlParams.get('variant');

      if (variantId) {
        this.indicators.forEach(indicator => {
          if (indicator.variantInventory && indicator.variantInventory[variantId]) {
            this.updateIndicator(indicator, variantId);
          }
        });
      }
    },

    /**
     * Update the indicator message for a specific variant
     * @param {HTMLElement} indicator - The indicator element
     * @param {string|number} variantId - The variant ID
     */
    updateIndicator: function(indicator, variantId) {
      const messageEl = indicator.querySelector('[data-low-stock-message]');
      if (!messageEl) return;

      const inventory = indicator.variantInventory;
      if (!inventory || !inventory[variantId]) {
        messageEl.textContent = '';
        indicator.classList.remove('low-stock-indicator--visible');
        return;
      }

      const variantData = inventory[variantId];
      const quantity = variantData.inventory_quantity;
      const threshold = parseInt(indicator.dataset.threshold, 10);
      const veryLowThreshold = parseInt(indicator.dataset.veryLowThreshold, 10);
      const displayMode = indicator.dataset.displayMode;

      // Check if we should show the indicator
      if (!variantData.inventory_management || quantity > threshold || quantity <= 0 || !variantData.available) {
        messageEl.textContent = '';
        indicator.classList.remove('low-stock-indicator--visible');
        return;
      }

      // Generate the message based on display mode
      let message = '';

      if (displayMode === 'exact') {
        // Exact mode with urgency-based prefix/suffix
        if (quantity === 1) {
          // Use the special "last one" message for exact mode
          message = indicator.dataset.exactLastOne;
        } else if (quantity <= veryLowThreshold) {
          // Very low stock - use urgent prefix/suffix
          const prefix = indicator.dataset.exactVeryLowPrefix;
          const suffix = indicator.dataset.exactVeryLowSuffix;
          message = `${prefix} ${quantity} ${suffix}`;
        } else {
          // Normal low stock - use standard prefix/suffix
          const prefix = indicator.dataset.exactPrefix;
          const suffix = indicator.dataset.exactSuffix;
          message = `${prefix} ${quantity} ${suffix}`;
        }
      } else {
        // Wording-only mode
        if (quantity === 1) {
          message = indicator.dataset.wordingLastOne;
        } else if (quantity <= veryLowThreshold) {
          message = indicator.dataset.wordingVeryLow;
        } else {
          message = indicator.dataset.wordingLow;
        }
      }

      messageEl.textContent = message;
      indicator.classList.add('low-stock-indicator--visible');

      // Add urgency classes for styling hooks
      indicator.classList.remove('low-stock-indicator--low', 'low-stock-indicator--very-low', 'low-stock-indicator--last-one');

      if (quantity === 1) {
        indicator.classList.add('low-stock-indicator--last-one');
      } else if (quantity <= veryLowThreshold) {
        indicator.classList.add('low-stock-indicator--very-low');
      } else {
        indicator.classList.add('low-stock-indicator--low');
      }

      // Dispatch custom event for other scripts to hook into
      indicator.dispatchEvent(new CustomEvent('lowstock:updated', {
        bubbles: true,
        detail: {
          variantId: variantId,
          quantity: quantity,
          message: message,
          urgencyLevel: quantity === 1 ? 'last-one' : (quantity <= veryLowThreshold ? 'very-low' : 'low')
        }
      }));
    }
  };

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => LowStockIndicator.init());
  } else {
    LowStockIndicator.init();
  }

  // Also reinitialize after Shopify section rendering (for Section Rendering API)
  document.addEventListener('shopify:section:load', () => {
    LowStockIndicator.init();
  });

  // Expose globally for external access if needed
  window.LowStockIndicator = LowStockIndicator;

  /**
   * Quick View Integration
   * Handles low stock indicator in modal quick view
   */
  const QuickViewLowStock = {
    indicator: null,
    variantInventory: {},

    /**
     * Initialize quick view low stock support
     * Called when quick view product data is loaded
     * @param {Object} product - The product JSON data from Shopify
     */
    init: function(product) {
      this.indicator = document.querySelector('#quickview-low-stock-indicator');
      if (!this.indicator) return;

      // Build variant inventory map from product data
      // Note: Shopify's product.json doesn't include inventory_quantity for security
      // We use global_quantity which is populated from data-quantity attribute on quick view buttons
      this.variantInventory = {};
      if (product && product.variants) {
        product.variants.forEach(variant => {
          // Get quantity from global_quantity (set by quick view button data-quantity)
          const qty = window.global_quantity && window.global_quantity[variant.id]
            ? parseInt(window.global_quantity[variant.id], 10)
            : 0;

          this.variantInventory[variant.id] = {
            inventory_quantity: qty,
            inventory_management: variant.inventory_management || 'shopify',
            inventory_policy: variant.inventory_policy || 'deny',
            available: variant.available
          };
        });
      }

      // Store inventory on the indicator element for the update function
      this.indicator.variantInventory = this.variantInventory;
    },

    /**
     * Update the quick view indicator for a specific variant
     * @param {string|number} variantId - The variant ID
     */
    update: function(variantId) {
      if (!this.indicator) {
        this.indicator = document.querySelector('#quickview-low-stock-indicator');
      }
      if (!this.indicator) return;

      // Use the main LowStockIndicator's update method
      if (this.indicator.variantInventory) {
        LowStockIndicator.updateIndicator(this.indicator, variantId);
      }
    },

    /**
     * Clear the quick view indicator
     */
    clear: function() {
      if (!this.indicator) {
        this.indicator = document.querySelector('#quickview-low-stock-indicator');
      }
      if (!this.indicator) return;

      const messageEl = this.indicator.querySelector('[data-low-stock-message]');
      if (messageEl) {
        messageEl.textContent = '';
      }
      this.indicator.classList.remove('low-stock-indicator--visible');
      this.variantInventory = {};
    }
  };

  // Expose quick view handler globally
  window.QuickViewLowStock = QuickViewLowStock;
})();
